import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, CircularProgress, IconButton, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import CallMadeIcon from '@material-ui/icons/CallMade';
import CallReceivedIcon from '@material-ui/icons/CallReceived';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { useTranslation } from 'react-i18next';
import {
  getMessageBody,
  getMessageAttachments,
  getAttachmentContent,
  getMailOrbitErrorKey
} from 'src/utils/MailOrbitService';
import { sanitizeEmailHtml } from 'src/utils/sanitizeEmailHtml';
import useStyles from './useStyles';
import { formatDateTime, formatSize, saveBlob, isViewableType, openBlob } from './format';

// Tıklama bir düğme/bağlantı üzerindeyse kartın kendi aç/kapa davranışı çalışmaz.
const isInteractiveTarget = e => !!(e.target.closest && e.target.closest('button, a'));

const MessageCard = ({
  conversationId,
  message,
  selected,
  onSelect,
  defaultExpanded,
  users,
  ownerUserId,
  isOwnedByMe
}) => {
  const classes = useStyles();
  const { t } = useTranslation(['mailorbit_workorder', 'mailorbit']);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [body, setBody] = useState(null); // { html } | { reasonCode }
  const [attachments, setAttachments] = useState(null);
  const [errorKey, setErrorKey] = useState(null);

  const outbound = message.direction === 'Outbound';
  const masked = message.isMasked === true;
  const deleted = message.isDeleted === true;
  const canExpand = !masked && !deleted;
  const when = formatDateTime(
    outbound ? message.sentAtUtc || message.receivedAtUtc : message.receivedAtUtc
  );

  // Gövde ve ekler yalnızca kart açıldığında (anlık) okunur; Sirius'ta saklanmaz.
  const loadBody = async () => {
    if (body || loading) return;
    setLoading(true);
    setErrorKey(null);
    try {
      const [bodyRes, attRes] = await Promise.all([
        getMessageBody(conversationId, message.conversationMessageId),
        message.hasAttachments
          ? getMessageAttachments(conversationId, message.conversationMessageId)
          : Promise.resolve(null)
      ]);
      setBody(
        bodyRes && bodyRes.success
          ? { html: sanitizeEmailHtml(bodyRes.sanitizedHtml) }
          : { reasonCode: (bodyRes && bodyRes.reasonCode) || 'unknown' }
      );
      setAttachments(attRes && attRes.success ? attRes.attachments || [] : []);
    } catch (e) {
      setErrorKey(getMailOrbitErrorKey(e));
      setExpanded(false);
    } finally {
      setLoading(false);
    }
  };

  // Başlık, önizleme ve "Mesajı göster" mesajı seçer (müşteri paneli buna göre değişir) ve aç/kapar.
  const toggleExpand = e => {
    if (e) e.stopPropagation();
    onSelect(message);
    if (!canExpand) return;
    const next = !expanded;
    setExpanded(next);
    if (next) loadBody();
  };

  // Varsayılan açık kart (son gelen mesaj): ilk çizimde gövdeyi oku.
  const autoOpened = useRef(false);
  useEffect(() => {
    if (defaultExpanded && canExpand && !autoOpened.current) {
      autoOpened.current = true;
      setExpanded(true);
      loadBody();
    }
    // yalnızca ilk çizimde
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAttachment = async (att, download) => {
    setErrorKey(null);
    try {
      const blob = await getAttachmentContent(
        conversationId,
        message.conversationMessageId,
        att.providerAttachmentId,
        download
      );
      if (download || !isViewableType(att.contentType)) {
        saveBlob(blob, att.fileName || 'attachment');
      } else {
        openBlob(blob, att.contentType);
      }
    } catch (e) {
      setErrorKey(getMailOrbitErrorKey(e));
    }
  };

  const to = (message.toRecipients || []).map(r => r.name || r.address).join(', ');

  // Kim: yalnızca mesajın taşıdığı veri kullanılır. Gönderen kullanıcı kimliği sahip/atama
  // listesinden çözülür; çözülemezse genel "Agent" etiketine düşülür.
  const sentById = message.sentByUserId || null;
  const sentByName = sentById
    ? (isOwnedByMe && sentById === ownerUserId ? t('mailorbit_workorder:you') : users[sentById]) ||
      t('mailorbit_workorder:agent')
    : null;
  const senderLabel = message.senderName || message.senderAddress || '-';
  const senderFull =
    message.senderName && message.senderAddress
      ? `${message.senderName} <${message.senderAddress}>`
      : senderLabel;
  let authorName;
  let authorLine;
  if (outbound) {
    authorName = sentByName || senderLabel;
    authorLine = sentById
      ? t('mailorbit_workorder:sent_by', { name: sentByName })
      : t('mailorbit_workorder:sent_from', { name: senderFull });
  } else {
    authorName = senderLabel;
    authorLine = t('mailorbit_workorder:received_from', { name: senderFull });
  }
  if (to) authorLine += ` ${t('mailorbit_workorder:to_recipient', { recipient: to })}`;

  return (
    <Box
      className={`${classes.card} ${outbound ? classes.cardOut : classes.cardIn} ${
        selected ? classes.cardSelected : ''
      }`}
    >
      <Box
        className={classes.cardHead}
        onClick={e => {
          if (!isInteractiveTarget(e)) toggleExpand();
        }}
        onKeyDown={e => {
          if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            toggleExpand();
          }
        }}
        role="button"
        tabIndex={0}
      >
        {canExpand ? (
          <IconButton
            size="small"
            onClick={toggleExpand}
            aria-expanded={expanded}
            aria-label={t(expanded ? 'mailorbit_workorder:collapse_message' : 'mailorbit_workorder:expand_message')}
            title={t(expanded ? 'mailorbit_workorder:collapse_message' : 'mailorbit_workorder:expand_message')}
          >
            <ChevronRightIcon
              fontSize="small"
              style={{ transition: 'transform .15s', transform: expanded ? 'rotate(90deg)' : 'none' }}
            />
          </IconButton>
        ) : (
          <Box style={{ width: 28, flex: 'none' }} />
        )}
        <Box className={classes.cardWho}>
          <Typography className={classes.cardSender}>
            {masked ? t('mailorbit_workorder:masked_message') : authorName}
          </Typography>
          {!masked && <Typography className={classes.cardAddr}>{authorLine}</Typography>}
        </Box>
        <span className={`${classes.dirPill} ${outbound ? classes.dirPillOut : ''}`}>
          {outbound ? (
            <CallMadeIcon className={classes.dirIcon} />
          ) : (
            <CallReceivedIcon className={classes.dirIcon} />
          )}
          {t(outbound ? 'mailorbit_workorder:direction_sent' : 'mailorbit_workorder:direction_received')}
        </span>
        {message.hasAttachments && !masked && (
          <AttachFileIcon fontSize="small" className={classes.iconMuted} />
        )}
        <Typography className={classes.cardDate}>{when}</Typography>
      </Box>
      {deleted && (
        <Box className={classes.cardBody}>
          <Typography variant="body2" color="textSecondary">
            {t('mailorbit_workorder:deleted_message')}
          </Typography>
        </Box>
      )}
      {masked && (
        <Box className={classes.cardBody}>
          <Typography variant="body2" color="textSecondary">
            {t('mailorbit_workorder:masked_message_hint')}
          </Typography>
        </Box>
      )}
      {errorKey && (
        <Alert severity="error" style={{ margin: 8 }}>
          {t(`mailorbit:${errorKey}`)}
        </Alert>
      )}
      {canExpand && !expanded && (
        <Box
          className={classes.cardPreview}
          onClick={e => {
            if (!isInteractiveTarget(e)) toggleExpand();
          }}
        >
          <Typography className={classes.cardPreviewText} title={message.subject || ''}>
            {message.subject || t('mailorbit_workorder:no_subject')}
          </Typography>
          {loading ? (
            <CircularProgress size={14} />
          ) : (
            <Button
              size="small"
              color="primary"
              className={classes.cardShowBtn}
              onClick={toggleExpand}
            >
              {t('mailorbit_workorder:show_body')}
            </Button>
          )}
        </Box>
      )}
      {expanded && (
        <Box className={classes.cardBody} onClick={() => onSelect(message)}>
          {loading && <CircularProgress size={20} style={{ marginTop: 12 }} />}
          {body && body.html !== undefined && (
            <div className={classes.html} dangerouslySetInnerHTML={{ __html: body.html }} />
          )}
          {body && body.reasonCode && (
            <Alert severity="warning" style={{ marginTop: 12 }}>
              {t('mailorbit_workorder:body_unavailable')} ({body.reasonCode})
            </Alert>
          )}
          {attachments && attachments.length > 0 && (
            <Box style={{ marginTop: 12 }}>
              <Typography className={classes.sideTitle}>
                {t('mailorbit_workorder:attachments')}
              </Typography>
              {attachments.map(att => (
                <Box key={att.providerAttachmentId} className={classes.attachment}>
                  <AttachFileIcon fontSize="small" className={classes.iconMuted} />
                  <Typography variant="body2" component="span">
                    {att.fileName} {att.sizeBytes ? `(${formatSize(att.sizeBytes)})` : ''}
                  </Typography>
                  {isViewableType(att.contentType) && (
                    <Button size="small" onClick={() => handleAttachment(att, false)}>
                      {t('mailorbit_workorder:view')}
                    </Button>
                  )}
                  <Button size="small" color="primary" onClick={() => handleAttachment(att, true)}>
                    {t('mailorbit_workorder:download')}
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

MessageCard.propTypes = {
  conversationId: PropTypes.string.isRequired,
  message: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  defaultExpanded: PropTypes.bool,
  users: PropTypes.object,
  ownerUserId: PropTypes.string,
  isOwnedByMe: PropTypes.bool
};

MessageCard.defaultProps = {
  defaultExpanded: false,
  users: {},
  ownerUserId: null,
  isOwnedByMe: false
};

export default MessageCard;
